'use client';

import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import type { GetServiceIntakeFormsResponse } from '@borradh-workspace/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';

/**
 * Stable empty fallback. A fresh `[]` on every render (while the links load,
 * or after the request fails) gives `links` a new identity each time, and a
 * consumer that syncs state from it re-renders forever.
 */
const EMPTY_LINKS: GetServiceIntakeFormsResponse['forms'] = [];

export const getServiceIntakeFormsQueryOptions = (serviceId: string) =>
  queryOptions({
    // Under the intake-forms root, so a set-service mutation's invalidation of
    // `intakeForms.all()` also refreshes a service's links.
    queryKey: queryKeys.intakeForms.serviceForms(serviceId),
    queryFn: () =>
      apiClient.get<GetServiceIntakeFormsResponse>(
        `intake-forms/services/${serviceId}/forms`
      ),
    enabled: !!serviceId,
    staleTime: 0,
  });

export const useGetServiceIntakeForms = (serviceId: string) => {
  const query = useQuery(getServiceIntakeFormsQueryOptions(serviceId));
  return {
    links: query.data?.forms ?? EMPTY_LINKS,
    // The service's links have arrived. Unlike `isSuccess`, a failed
    // background refetch (which keeps the last good data) leaves it true.
    isLoaded: query.data !== undefined,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
